import React from "react";
import { useEffect, useRef } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Stack,
    SvgIcon,
    Typography,
} from "@mui/material";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FormularioNichos from "../niches/pages/components/FormularioNichos";
import { gerarLinkWhatsApp } from "../../utils/WhatsappLink";

/**
 * ContBlack — Economia Fiscal / Planejamento Tributário
 * Versão remodelada em 1 único componente .jsx
 * Stack: React / Next.js + MUI v5
 * Estilização: 100% inline via sx. Sem Tailwind, sem CSS externo e sem styled-components.
 *
 * Uso:
 * import EconomiaFiscalContBlackReajusteMUI from "./EconomiaFiscalContBlackReajusteMUI";
 *
 * export default function Page() {
 *   return (
 *     <EconomiaFiscalContBlackReajusteMUI
 *       logoSrc="/logo-contblack-fundo-escuro.png"
 *       ctaHref="https://wa.me/55..."
 *     />
 *   );
 * }
 *
 * Observação Next.js:
 * Como este componente usa "use client", a metadata SEO deve ficar no page.jsx ou layout.jsx da rota.
 */

const C = {
    dark: "#203040",
    dark2: "#18242F",
    dark3: "#101A24",
    green: "#00E676",
    green2: "#00C864",
    magenta: "#E00090",
    magenta2: "#BD007B",
    cyan: "#00D4FF",
    gold: "#FFD700",
    purple: "#9010B0",
    red: "#FF4757",
    white: "#FFFFFF",
    offWhite: "#F5F5F5",
    gray200: "#E0E0E0",
    gray700: "#424242",
    muted: "#B8C6D6",
    muted2: "#8EA0B3",
    line: "rgba(255,255,255,0.12)",
    glass: "rgba(255,255,255,0.065)",
};

const FONT_TITLE = "'Oswald', 'Barlow Condensed', 'Arial Narrow', sans-serif";
const FONT_BODY = "'Inter', 'Barlow', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const HERO_BULLETS = [
    "Chega de deixar dinheiro na mesa todo mês",
    "Créditos, incentivos e regimes que você nunca revisou",
    "Para CNPJ, sócios e quem ganha bem como pessoa física",
    "Estratégia real, sem atalho e sem enrolação",
];

const DOR_CARDS = [
    {
        label: "Regime",
        title: "Regime errado: o Leão agradece",
        desc: "Estar no modelo tributário inadequado é pagar mais todo mês sem perceber. Isso tem nome: desperdício fiscal.",
    },
    {
        label: "Créditos",
        title: "Dinheiro parado que era seu",
        desc: "Compras, insumos e operações podem gerar crédito de PIS e COFINS. A maioria nunca revisou isso.",
    },
    {
        label: "Fator R",
        title: "Fator R: a conta que poucos fazem",
        desc: "Quando a folha é baixa em relação ao faturamento, a alíquota do Simples pode subir sem aviso.",
    },
    {
        label: "Carnê-Leão",
        title: "Pagar 27,5% sem estratégia dói",
        desc: "Profissional liberal com Livro Caixa mal aproveitado paga mais imposto do que precisa. Todo mês.",
    },
    {
        label: "Lucros",
        title: "Distribuir lucro sem planejar é cilada",
        desc: "Decisões de retirada, pró-labore e distribuição precisam considerar impacto fiscal, documentação e risco.",
    },
    {
        label: "Benefícios",
        title: "Incentivo fiscal que ninguém te contou",
        desc: "Lei do Bem, depreciação acelerada e incentivos regionais existem e quase ninguém aproveita.",
    },
    {
        label: "Reforma",
        title: "2026 é ano de decidir. 2027 é tarde",
        desc: "A transição tributária exige simulação antes do impacto chegar no caixa. Quem se antecipa decide melhor.",
    },
    {
        label: "Decisão",
        title: "Decidir no improviso é o imposto mais caro",
        desc: "Cada mudança de regime, contratação ou distribuição mexe na tributação. Quem não calcula, paga.",
    },
];

const SOLUTION_STEPS = [
    {
        n: "01",
        title: "A gente olha tudo, do zero",
        body: "Regime, faturamento, despesas, folha, notas, pró-labore e histórico fiscal. Sem chute, sem suposição.",
        color: C.green,
    },
    {
        n: "02",
        title: "Mapeamos onde está o dinheiro",
        body: "Créditos que nunca foram pedidos, regimes que nunca foram comparados, incentivos que nunca foram testados.",
        color: C.cyan,
    },
    {
        n: "03",
        title: "Só recomendamos o que tem lastro",
        body: "Cada oportunidade passa pelo filtro da legalidade, documentação e risco. O Leão está de olho, e nós também.",
        color: C.green,
    },
    {
        n: "04",
        title: "Você recebe um plano, não um PDF genérico",
        body: "O que fazer, em qual ordem, quais documentos juntar e o que evitar. Claro, direto e executável.",
        color: C.cyan,
    },
    {
        n: "05",
        title: "Acompanhamos até o fim",
        body: "Não sumimos depois do diagnóstico. Apoiamos a execução com controle, rastreabilidade e segurança.",
        color: C.gold,
    },
];

const CNPJ_ITEMS = [
    "Simples Nacional x Lucro Presumido x Lucro Real",
    "Fator R e estrutura de folha e pró-labore",
    "Créditos de PIS e COFINS e oportunidades fiscais",
    "Benefícios fiscais e incentivos aplicáveis",
    "Tributação sobre produtos, serviços e operações",
    "Impactos da Reforma Tributária",
    "Revisão de classificação fiscal e rotinas tributárias",
    "Planejamento de margem e caixa",
];

const CPF_ITEMS = [
    "Carnê-Leão e Livro Caixa",
    "Deduções legais no IRPF",
    "Transição de CPF para CNPJ quando fizer sentido",
    "Pró-labore, lucros e distribuição de resultados",
    "Previdência privada e planejamento de IR",
    "Organização de renda profissional e patrimonial",
    "Renda do exterior e obrigações fiscais",
    "Estratégia de declaração e documentação",
];

const BENEFITS = [
    {
        icon: "◎",
        title: "Mais margem",
        body: "Regime certo, créditos aproveitados e estrutura revisada. O que sobra fica no seu caixa, não no do Leão.",
        color: C.green,
    },
    {
        icon: "◈",
        title: "Mais previsibilidade",
        body: "Você para de tomar decisão no escuro. Sabe o impacto antes de contratar, crescer ou distribuir lucro.",
        color: C.cyan,
    },
    {
        icon: "◉",
        title: "Mais segurança",
        body: "Cada recomendação tem base legal e documentação. Nada de atalho, nada que o Leão possa questionar lá na frente.",
        color: C.green,
    },
    {
        icon: "◐",
        title: "Mais caixa",
        body: "Créditos não aproveitados, incentivos esquecidos e regimes inadequados são dinheiro parado. Recuperar isso é possível.",
        color: C.gold,
    },
    {
        icon: "◑",
        title: "Mais controle",
        body: "Você passa a enxergar a sua carga tributária de verdade. Sem surpresa, sem susto no fechamento do mês.",
        color: C.purple,
    },
    {
        icon: "◇",
        title: "Mais estratégia",
        body: "O fiscal deixa de ser só obrigação e passa a apoiar decisões de preço, crescimento, distribuição e investimento.",
        color: C.gold,
    },
];

const FAZEMOS = [
    "Diagnóstico real, com os dados do cliente na mesa",
    "Estratégia feita para aquela operação, não para qualquer um",
    "Documentação e lastro antes de qualquer recomendação",
    "Acompanhamento da execução do começo ao fim",
    "Linguagem humana para o cliente entender cada passo",
];

const NAO_FAZEMOS = [
    "Prometer percentual antes de ver os números",
    "Sugerir atalho que o Leão pode questionar depois",
    "Economia no papel sem base documental real",
    "Recomendação copiada e colada de outro cliente",
    "Pressão para mudar de regime sem comparativo feito",
];

const OBJECTIONS = [
    {
        q: "Isso é legal mesmo?",
        a: "Sim. Tem nome técnico: elisão fiscal. É planejamento dentro da lei, com documentação e sem simulação. O oposto disso é sonegação, e isso a gente não faz.",
    },
    {
        q: "O Leão não vai vir atrás de mim?",
        a: "Um planejamento bem feito reduz exatamente esse risco. Imprevisto, improviso e decisão sem documentação são o que atrai autuação.",
    },
    {
        q: "Minha empresa é pequena, vale a pena?",
        a: "A maioria das economias começa em ajustes simples: regime, fator R, pró-labore, classificação de receita. Pequena empresa paga imposto errado também.",
    },
    {
        q: "E se não tiver economia nenhuma?",
        a: "Você sai com clareza. Saber que a estrutura atual está certa também tem valor. Pelo menos você para de ficar na dúvida.",
    },
    {
        q: "Vocês garantem quanto vou economizar?",
        a: "Não garantimos número antes de ver os dados. Quem promete percentual sem diagnóstico está chutando, não consultando.",
    },
];

const FAQS = [
    {
        q: "É possível pagar menos impostos legalmente?",
        a: "Sim, e mais gente deveria fazer isso. A lei permite diferentes regimes, deduções, créditos e incentivos. O trabalho é descobrir o que se aplica ao seu caso, com segurança e documentação.",
    },
    {
        q: "Qual a diferença entre elisão fiscal e sonegação?",
        a: "Elisão fiscal é planejamento dentro da lei. Sonegação é esconder, falsificar ou declarar errado de propósito. São coisas completamente diferentes, e a gente trabalha só com a primeira.",
    },
    {
        q: "Minha empresa está no Simples Nacional. Ainda assim dá para economizar?",
        a: "Pode dar, sim. Fator R, anexos, pró-labore, folha e enquadramento têm impacto direto na alíquota. E em alguns casos sair do Simples pode ser mais vantajoso, mas isso só aparece com cálculo real.",
    },
    {
        q: "Lucro Presumido sempre paga menos que Simples?",
        a: "Não tem resposta certa sem ver os números. Depende de atividade, margem, folha, créditos e operação. Comparar regime sem cálculo é chute.",
    },
    {
        q: "Lucro Real só vale para empresa grande?",
        a: "Não necessariamente. Empresa com margem baixa, muitas despesas ou créditos aproveitáveis pode se sair melhor no Lucro Real. Vale a análise.",
    },
    {
        q: "Planejamento tributário aumenta risco fiscal?",
        a: "Quando é mal feito, sim. Quando tem diagnóstico, documentação e base legal, o resultado é o oposto: menos imprevisto, mais controle.",
    },
    {
        q: "Pessoa física pode fazer planejamento tributário?",
        a: "Pode e deve, principalmente quem ganha bem. Carnê-Leão, Livro Caixa, deduções, previdência privada e a decisão entre CPF e CNPJ fazem diferença real na conta.",
    },
    {
        q: "Abrir CNPJ sempre reduz imposto para autônomo?",
        a: "Não é automático. Depende da atividade, do faturamento, dos custos e das obrigações que vêm junto com o CNPJ. Às vezes vale, às vezes não. Sem cálculo, não tem como saber.",
    },
    {
        q: "O que é fator R?",
        a: "É a relação entre folha de pagamento e receita bruta. Quando essa conta chega a 28% ou mais, algumas atividades no Simples Nacional mudam de anexo e pagam menos imposto.",
    },
    {
        q: "Dá para recuperar imposto pago a mais?",
        a: "Em alguns casos, sim. Depende do tributo, do período, da documentação e da origem do pagamento. Mas existe esse caminho, e vale verificar.",
    },
    {
        q: "Quanto minha empresa pode economizar?",
        a: "Essa resposta só existe depois do diagnóstico. Quem responde sem ver os dados está inventando um número. A gente não faz isso.",
    },
    {
        q: "O diagnóstico é seguro e sigiloso?",
        a: "Sim. As informações são tratadas com sigilo, organização documental e foco em conformidade. Nada sai sem autorização.",
    },
];

function ChevronDownIcon(props) {
    return (
        <SvgIcon {...props} viewBox="0 0 24 24">
            <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </SvgIcon>
    );
}

function FloatingGlow({ sx }) {
    return (
        <Box
            aria-hidden="true"
            sx={{
                position: "absolute",
                width: 340,
                height: 340,
                borderRadius: "999px",
                filter: "blur(74px)",
                opacity: 0.34,
                pointerEvents: "none",
                ...sx,
            }}
        />
    );
}

function Logo({ src, sx = {} }) {
    return (
        <Box
            component="img"
            src={src}
            alt="ContBlack"
            sx={{
                display: "block",
                width: { xs: 132, md: 158 },
                height: "auto",
                ...sx,
            }}
        />
    );
}

function SectionLabel({ children, color = C.green, center = false }) {
    return (
        <Typography
            component="p"
            sx={{
                fontFamily: FONT_BODY,
                fontWeight: 950,
                fontSize: { xs: 11, md: 12 },
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color,
                mb: 1.5,
                textAlign: center ? "center" : "inherit",
            }}
        >
            {children}
        </Typography>
    );
}

function SectionTitle({ children, dark = false, center = false, maxWidth = 820 }) {
    return (
        <Typography
            component="h2"
            sx={{
                fontFamily: FONT_TITLE,
                fontWeight: 950,
                fontSize: { xs: 30, sm: 35, md: 42 },
                lineHeight: 1.02,
                letterSpacing: "-0.048em",
                color: dark ? C.dark : C.white,
                textAlign: center ? "center" : "inherit",
                maxWidth,
                mx: center ? "auto" : 0,
            }}
        >
            {children}
        </Typography>
    );
}

function BodyText({ children, dark = false, sx = {} }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_BODY,
                fontSize: { xs: 15.5, md: 17 },
                lineHeight: 1.75,
                color: dark ? "rgba(32,48,64,0.72)" : "rgba(255,255,255,0.70)",
                ...sx,
            }}
        >
            {children}
        </Typography>
    );
}

function PrimaryButton({ action, children, sx = {} }) {
    return (
        <Button
            onClick={action}
            variant="contained"
            disableElevation
            sx={{
                px: 3.4,
                py: 1.45,
                borderRadius: 2.2,
                background: `linear-gradient(135deg, ${C.green} 0%, #1DFF8A 100%)`,
                color: C.dark,
                fontFamily: FONT_BODY,
                fontWeight: 950,
                fontSize: 14,
                textTransform: "none",
                boxShadow: "0 18px 46px rgba(0,230,118,0.24)",
                "&:hover": {
                    background: `linear-gradient(135deg, #25FF91 0%, ${C.green2} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: "0 22px 52px rgba(0,230,118,0.30)",
                },
                "&:active": { transform: "translateY(0) scale(.99)" },
                ...sx,
            }}
        >
            {children}
        </Button>
    );
}

function MagentaButton({ action, children, sx = {} }) {
    return (
        <Button
            onClick={action}
            variant="contained"
            disableElevation
            sx={{
                px: 3.4,
                py: 1.45,
                borderRadius: 2.2,
                background: `linear-gradient(135deg, ${C.magenta} 0%, ${C.purple} 100%)`,
                color: C.white,
                fontFamily: FONT_BODY,
                fontWeight: 950,
                fontSize: 14,
                textTransform: "none",
                boxShadow: "0 18px 46px rgba(224,0,144,0.25)",
                "&:hover": {
                    background: `linear-gradient(135deg, #F000A0 0%, ${C.purple} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: "0 22px 52px rgba(224,0,144,0.32)",
                },
                "&:active": { transform: "translateY(0) scale(.99)" },
                ...sx,
            }}
        >
            {children}
        </Button>
    );
}

function OutlineButton({ action, children, color = C.green, sx = {} }) {
    return (
        <Button
            onClick={action}
            variant="outlined"
            sx={{
                px: 3.4,
                py: 1.45,
                borderRadius: 2.2,
                borderColor: color,
                color,
                fontFamily: FONT_BODY,
                fontWeight: 850,
                fontSize: 14,
                textTransform: "none",
                backgroundColor: "rgba(255,255,255,0.02)",
                "&:hover": {
                    borderColor: color,
                    backgroundColor: `${color}16`,
                    transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0) scale(.99)" },
                ...sx,
            }}
        >
            {children}
        </Button>
    );
}

function PainCard({ label, title, desc }) {
    return (
        <Card
            elevation={0}
            sx={{
                width: window.innerWidth < 600 ? "150px" : "270px",
                height: "100%",
                position: "relative",
                overflow: "hidden",
                borderRadius: 4,
                border: "1px solid rgba(224,0,144,0.24)",
                background:
                    "linear-gradient(145deg, rgba(224,0,144,0.105) 0%, rgba(255,255,255,0.038) 100%)",
                transition: "all .22s ease",
                "&:before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: "radial-gradient(circle at top right, rgba(224,0,144,0.22), transparent 45%)",
                    pointerEvents: "none",
                },
                "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(224,0,144,0.55)",
                    boxShadow: "0 24px 70px rgba(0,0,0,0.26), 0 0 0 1px rgba(224,0,144,0.18)",
                },
            }}
        >
            <CardContent sx={{ p: 2.6, position: "relative", zIndex: 1 }}>
                <Chip
                    label={label}
                    size="small"
                    sx={{
                        height: 23,
                        mb: 1.6,
                        color: C.white,
                        backgroundColor: "rgba(224,0,144,0.78)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        fontFamily: FONT_BODY,
                        fontSize: 11,
                        fontWeight: 900,
                    }}
                />
                <Typography
                    component="h3"
                    sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 950,
                        color: C.white,
                        fontSize: 16,
                        lineHeight: 1.25,
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT_BODY,
                        color: "rgba(255,255,255,0.58)",
                        fontSize: 13.3,
                        lineHeight: 1.7,
                    }}
                >
                    {desc}
                </Typography>
            </CardContent>
        </Card>
    );
}

function StepCard({ n, title, body, color }) {
    return (
        <Box
            sx={{
                width: window.innerWidth < 600 ? "150px" : "300px",
                height: "100%",
                position: "relative",
                overflow: "hidden",
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(255,255,255,0.11)",
                borderTop: `3px solid ${color}`,
                background: "linear-gradient(145deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.035) 100%)",
                transition: "all .22s ease",
                "&:before": {
                    content: '""',
                    position: "absolute",
                    width: 160,
                    height: 160,
                    borderRadius: "999px",
                    right: -82,
                    top: -82,
                    backgroundColor: `${color}22`,
                },
                "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: `${color}75`,
                    boxShadow: `0 24px 70px rgba(0,0,0,0.25), 0 0 40px ${color}1F`,
                },
            }}
        >
            <Typography
                sx={{
                    position: "relative",
                    fontFamily: FONT_TITLE,
                    fontWeight: 950,
                    fontSize: 42,
                    lineHeight: 1,
                    color,
                    mb: 2,
                    letterSpacing: "-0.05em",
                }}
            >
                {n}
            </Typography>
            <Typography
                component="h3"
                sx={{
                    position: "relative",
                    fontFamily: FONT_BODY,
                    fontWeight: 950,
                    fontSize: 16,
                    lineHeight: 1.25,
                    color: C.white,
                    mb: 1.2,
                }}
            >
                {title}
            </Typography>
            <Typography
                sx={{
                    position: "relative",
                    fontFamily: FONT_BODY,
                    fontSize: 13.2,
                    lineHeight: 1.7,
                    color: "rgba(255,255,255,0.60)",
                }}
            >
                {body}
            </Typography>
        </Box>
    );
}

function PublicCard({ type, title, desc, items, color, cta, action, href }) {
    return (
        <Box
            sx={{
                height: "100%",
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                backgroundColor: C.white,
                border: "1px solid rgba(32,48,64,0.10)",
                borderTop: `5px solid ${color}`,
                boxShadow: "0 26px 70px rgba(32,48,64,0.10)",
            }}
        >
            <Chip
                label={type}
                sx={{
                    mb: 2,
                    backgroundColor: `${color}18`,
                    border: `1px solid ${color}55`,
                    color: C.dark,
                    fontFamily: FONT_BODY,
                    fontWeight: 950,
                    fontSize: 12,
                }}
            />

            <Typography
                component="h3"
                sx={{
                    fontFamily: FONT_TITLE,
                    fontWeight: 950,
                    color: C.dark,
                    fontSize: { xs: 25, md: 30 },
                    lineHeight: 1.08,
                    letterSpacing: "-0.035em",
                    mb: 1.5,
                }}
            >
                {title}
            </Typography>

            <Typography
                sx={{
                    fontFamily: FONT_BODY,
                    color: "rgba(32,48,64,0.72)",
                    fontSize: 14.5,
                    lineHeight: 1.75,
                    mb: 2.6,
                }}
            >
                {desc}
            </Typography>

            <Divider sx={{ borderColor: "rgba(32,48,64,0.10)", mb: 2.5 }} />

            <Stack spacing={1.25}>
                {items.map((item) => (
                    <Stack direction="row" spacing={1.3} alignItems="flex-start" key={item}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                mt: 0.85,
                                borderRadius: "999px",
                                flexShrink: 0,
                                backgroundColor: color,
                                boxShadow: `0 0 14px ${color}80`,
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: FONT_BODY,
                                color: "rgba(32,48,64,0.76)",
                                fontSize: 13.7,
                                lineHeight: 1.55,
                            }}
                        >
                            {item}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

            <Button
                onClick={action}
                variant="outlined"
                sx={{
                    mt: 3.3,
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    borderColor: color,
                    color: C.dark,
                    fontFamily: FONT_BODY,
                    fontWeight: 950,
                    textTransform: "none",
                    "&:hover": { borderColor: color, backgroundColor: `${color}14` },
                }}
            >
                {cta}
            </Button>
        </Box>
    );
}

function BenefitCard({ icon, title, body, color }) {
    return (
        <Card
            elevation={0}
            sx={{
                width: window.innerWidth < 600 ? "100%" : "350px",
                height: "100%",
                borderRadius: 4,
                border: "1px solid rgba(255,255,255,0.10)",
                borderLeft: `4px solid ${color}`,
                background: "linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)",
                transition: "all .22s ease",
                "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: `${color}66`,
                    boxShadow: `0 24px 70px rgba(0,0,0,0.25), 0 0 34px ${color}1E`,
                },
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Typography sx={{ color, fontSize: 34, lineHeight: 1, mb: 1.7 }}>
                    {icon}
                </Typography>
                <Typography
                    component="h3"
                    sx={{ fontFamily: FONT_BODY, fontWeight: 950, color: C.white, fontSize: 17, mb: 1 }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.60)", fontSize: 13.5, lineHeight: 1.72 }}
                >
                    {body}
                </Typography>
            </CardContent>
        </Card>
    );
}

function CheckListCard({ title, items, color, negative = false }) {
    return (
        <Box
            sx={{
                height: "100%",
                borderRadius: 4,
                border: `1px solid ${color}44`,
                background: `linear-gradient(145deg, ${color}12 0%, rgba(255,255,255,0.035) 100%)`,
                p: 3,
            }}
        >
            <Typography
                sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 950,
                    fontSize: 12,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color,
                    mb: 2.2,
                }}
            >
                {title}
            </Typography>

            <Stack spacing={1.5}>
                {items.map((item) => (
                    <Stack direction="row" spacing={1.3} alignItems="flex-start" key={item}>
                        <Avatar
                            sx={{
                                width: 21,
                                height: 21,
                                mt: 0.15,
                                flexShrink: 0,
                                backgroundColor: `${color}24`,
                                color,
                                border: `1px solid ${color}44`,
                                fontSize: 12,
                                fontWeight: 950,
                            }}
                        >
                            {negative ? "!" : "✓"}
                        </Avatar>
                        <Typography
                            sx={{ fontFamily: FONT_BODY, fontSize: 13.7, lineHeight: 1.6, color: "rgba(255,255,255,0.73)" }}
                        >
                            {item}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}

function ObjectionCard({ q, a }) {
    return (
        <Box
            sx={{
                height: "100%",
                p: 2.5,
                borderRadius: 3,
                border: "1px solid rgba(0,212,255,0.20)",
                background: "linear-gradient(145deg, rgba(0,212,255,0.08) 0%, rgba(255,255,255,0.035) 100%)",
            }}
        >
            <Typography
                component="h3"
                sx={{ fontFamily: FONT_BODY, fontWeight: 950, color: C.cyan, fontSize: 15, lineHeight: 1.3, mb: 1 }}
            >
                {q}
            </Typography>
            <Typography
                sx={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.62)", fontSize: 13.5, lineHeight: 1.72 }}
            >
                {a}
            </Typography>
        </Box>
    );
}

export default function Economize({ logoSrc = "assets/Logo_Contblack_FundoEscuro.png" }) {

    const secaoRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [])

    const irParaSecao = () => {
        secaoRef.current.scrollIntoView({
            behavior: "smooth",
        });
    };
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: C.dark,
                color: C.white,
                fontFamily: FONT_BODY,
                overflowX: "hidden",
            }}
        >
            <Header />

            <Box sx={{ mt: 3 }}>
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        pt: { xs: 7, md: 11 },
                        background: `
                            radial-gradient(circle at 6% 15%, rgba(0,230,118,0.18), transparent 34%),
                            radial-gradient(circle at 92% 18%, rgba(224,0,144,0.14), transparent 34%),
                            linear-gradient(180deg, ${C.dark} 0%, ${C.dark2} 100%)
                        `,
                    }}
                >
                    <FloatingGlow sx={{ left: -120, top: 40, backgroundColor: C.green }} />
                    <FloatingGlow sx={{ right: -110, top: 80, backgroundColor: C.magenta }} />

                    <Box sx={{ maxWidth: window.innerWidth < 900 ? "100%" : "65%", margin: "0 auto" }}>
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "space-between", gap: { xs: 4, md: 2.5 }, padding: { xs: 5, md: 2 } }}>
                            <Box sx={{ width: { xs: "100%", md: "55%" } }}>
                                <SectionLabel>Economia Fiscal · ContBlack</SectionLabel>

                                <Typography
                                    component="h1"
                                    sx={{
                                        maxWidth: 780,
                                        fontFamily: FONT_TITLE,
                                        fontWeight: 950,
                                        fontSize: { xs: 39, sm: 48, md: 68 },
                                        lineHeight: { xs: 1.02, md: 0.96 },
                                        letterSpacing: "-0.06em",
                                        color: C.white,
                                        mb: 2.4,
                                    }}
                                >
                                    Você pode estar pagando{" "}
                                    <Box component="span" sx={{ color: C.green, textShadow: "0 0 30px rgba(0,230,118,0.35)" }}>
                                        mais impostos
                                    </Box>{" "}
                                    do que deveria.
                                </Typography>

                                <BodyText sx={{ maxWidth: 680, mb: 3.4 }}>
                                    O Leão não liga para o quanto você trabalhou. Ele liga para o quanto você recebeu e movimentou!
                                    A diferença entre pagar certo e pagar demais é quase sempre uma análise que nunca foi feita.
                                </BodyText>

                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3.5, maxWidth: 680 }}>
                                    {HERO_BULLETS.map((bullet) => (
                                        <Chip
                                            key={bullet}
                                            label={bullet}
                                            sx={{
                                                height: 29,
                                                color: C.green,
                                                backgroundColor: "rgba(0,230,118,0.10)",
                                                border: "1px solid rgba(0,230,118,0.25)",
                                                fontFamily: FONT_BODY,
                                                fontSize: 12,
                                                fontWeight: 750,
                                            }}
                                        />
                                    ))}
                                </Stack>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ display: "flex", justifyContent: "flex-start", gap: 1.5, mb: 2.5 }}>
                                    <PrimaryButton action={irParaSecao}>Quero parar de deixar dinheiro na mesa</PrimaryButton>
                                </Stack>

                                <Typography sx={{ mt: 1.8, fontFamily: FONT_BODY, color: "rgba(255,255,255,0.44)", fontSize: 12.5, lineHeight: 1.65, maxWidth: 650 }}>
                                    Sem promessa de milagre. Sem percentual inventado. Primeiro a gente olha os dados, depois indica o caminho.
                                </Typography>
                            </Box>

                            <Box item xs={12} md={5.2}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        maxWidth: 380,
                                        borderRadius: 5,
                                        p: { xs: 2.4, md: 3.2 },
                                        border: "1px solid rgba(0,230,118,0.20)",
                                        background: "linear-gradient(145deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.045) 100%)",
                                        boxShadow: "0 32px 100px rgba(0,0,0,0.32)",
                                        backdropFilter: "blur(20px)",
                                        "&:before": {
                                            content: '""',
                                            position: "absolute",
                                            inset: 0,
                                            background: `
                                                radial-gradient(circle at 18% 10%, rgba(0,230,118,0.23), transparent 34%),
                                                radial-gradient(circle at 90% 90%, rgba(144,16,176,0.28), transparent 40%)
                                            `,
                                            pointerEvents: "none",
                                        },
                                    }}
                                >
                                    <Box sx={{ position: "relative", zIndex: 1 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                                            <Logo src={logoSrc} sx={{ width: 132 }} />
                                            <Chip
                                                label="Diagnóstico"
                                                sx={{
                                                    height: 28,
                                                    color: C.green,
                                                    fontFamily: FONT_BODY,
                                                    fontWeight: 950,
                                                    backgroundColor: "rgba(0,230,118,0.12)",
                                                    border: "1px solid rgba(0,230,118,0.28)",
                                                }}
                                            />
                                        </Stack>

                                        <Box sx={{ p: 2.4, borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)", backgroundColor: "rgba(0,0,0,0.22)" }}>
                                            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: C.muted, mb: 0.8 }}>
                                                Painel de oportunidades
                                            </Typography>
                                            <Typography sx={{ fontFamily: FONT_TITLE, fontWeight: 950, fontSize: 34, lineHeight: 1, letterSpacing: "-0.04em", color: C.white, mb: 1 }}>
                                                Dados antes de decisões.
                                            </Typography>
                                            <Typography sx={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.62)", fontSize: 13.5, lineHeight: 1.65, mb: 2.5 }}>
                                                Economia fiscal segura nasce de comparativo real: regime, margem, créditos, despesas, folha e risco.
                                            </Typography>

                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, }}>
                                                {[
                                                    ["Regime tributário", "Simples · Presumido · Real", C.green],
                                                    ["Créditos e incentivos", "PIS/COFINS · Benefícios", C.cyan],
                                                    ["CPF/CNPJ", "Sócios · Alta renda · Renda exterior", C.magenta],
                                                ].map(([label, desc, color]) => (
                                                    <Box
                                                        key={label}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: 2,
                                                            p: 1.35,
                                                            borderRadius: 2.2,
                                                            backgroundColor: "rgba(255,255,255,0.06)",
                                                            border: "1px solid rgba(255,255,255,0.08)",
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={1.1} alignItems="center">
                                                            <Box sx={{ width: 9, height: 9, borderRadius: "999px", backgroundColor: color, boxShadow: `0 0 16px ${color}` }} />
                                                            <Typography sx={{ fontFamily: FONT_BODY, color: C.white, fontSize: 13, fontWeight: 900 }}>
                                                                {label}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography sx={{ fontFamily: FONT_BODY, color: C.muted, fontSize: 12, textAlign: "right" }}>
                                                            {desc}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        <Box sx={{ mt: 2, p: 2, borderRadius: 3, background: `linear-gradient(135deg, ${C.magenta} 0%, ${C.purple} 100%)` }}>
                                            <Typography sx={{ fontFamily: FONT_BODY, color: C.white, fontWeight: 950, fontSize: 13 }}>
                                                Sem promessa vazia
                                            </Typography>
                                            <Typography sx={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.82)", fontSize: 12.5, mt: 0.5 }}>
                                                Percentual de economia só deve ser tratado depois da análise dos dados.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    component="section"
                    id="dor"
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        py: { xs: 8, md: 12 },
                        background: `radial-gradient(circle at 0% 20%, rgba(224,0,144,0.15), transparent 32%), ${C.dark2}`,
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ maxWidth: 880, mb: 5.5 }}>
                            <SectionLabel color={C.white}>O problema que ninguém vê</SectionLabel>
                            <SectionTitle>
                                O imposto pago a mais raramente aparece como{" "}
                                <Box component="span" sx={{ color: C.magenta, width: "100%" }}>
                                    um problema evidente.
                                </Box>
                            </SectionTitle>
                            <BodyText sx={{ mt: 2.2 }}>
                                O ralo fiscal raramente aparece em uma guia só. Ele drena todo mês, quietinho: regime errado,
                                crédito não pedido, alíquota desnecessária, decisão tomada sem olhar o impacto tributário.
                                Quando você percebe, já foi embora muito dinheiro.
                            </BodyText>
                            <BodyText sx={{ mt: 1.4 }}>
                                Para quem ganha bem como pessoa física, a conta dói do mesmo jeito. Profissional liberal pagando
                                27,5% sem estratégia, sócio distribuindo lucro sem planejamento, autônomo sem Livro Caixa.
                                O Leão está de olho, mas quem devia estar também é você.
                            </BodyText>
                        </Box>

                        <Box spacing={2.4} sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2.4, mb: 5 }}>
                            {DOR_CARDS.map((card) => (
                                <Box key={card.title}>
                                    <PainCard {...card} />
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 4, textAlign: "center", border: "1px solid rgba(224,0,144,0.25)", background: "linear-gradient(135deg, rgba(224,0,144,0.14) 0%, rgba(255,255,255,0.04) 100%)" }}>
                            <Typography sx={{ fontFamily: FONT_BODY, color: C.white, fontWeight: 900, fontSize: { xs: 17, md: 21 }, lineHeight: 1.55 }}>
                                O problema não é pagar impostos.{" "}
                                <Box component="span" sx={{ color: C.magenta }}>
                                    O problema é pagar mais do que a lei exige.
                                </Box>
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                <Box
                    component="section"
                    id="solucao"
                    sx={{
                        py: { xs: 8, md: 12 },
                        background: `
              radial-gradient(circle at 100% 12%, rgba(0,212,255,0.13), transparent 32%),
              radial-gradient(circle at 0% 80%, rgba(0,230,118,0.10), transparent 32%),
              ${C.dark}
            `,
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: "center", maxWidth: 850, mx: "auto", mb: 6 }}>
                            <SectionLabel center>Método ContBlack</SectionLabel>
                            <SectionTitle center>
                                Economia fiscal segura começa com{" "}
                                <Box component="span" sx={{ color: C.green }}>
                                    diagnóstico, não com chute.
                                </Box>
                            </SectionTitle>
                            <BodyText sx={{ mt: 2.2 }}>
                                Planejamento tributário não é macete. É diagnóstico, cruzamento de dados e identificação do que
                                a lei permite para a sua realidade. Sem inventar, sem arriscar, sem prometer o que não dá pra cumprir.
                            </BodyText>
                        </Box>

                        <Box spacing={2.4} sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2.4, mb: 5 }}>
                            {SOLUTION_STEPS.map((step) => (
                                <Box item xs={12} sm={6} md={step.n === "05" ? 4 : 3} key={step.n}>
                                    <StepCard {...step} />
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                <Box component="section" id="publicos" sx={{ py: { xs: 8, md: 12 }, backgroundColor: C.offWhite }}>
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: "center", maxWidth: 900, mx: "auto", mb: 6 }}>
                            <SectionLabel color={C.dark} center>Para quem é</SectionLabel>
                            <SectionTitle dark center maxWidth={920}>
                                Soluções diferentes para quem paga imposto como empresa e para quem paga como pessoa física.
                            </SectionTitle>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3 }}>
                            <Box sx={{ flex: "1 1 300px", maxWidth: 600 }}>
                                <PublicCard
                                    type="CNPJ"
                                    color={C.green}
                                    title="Para empresas que querem reduzir carga tributária sem aumentar risco fiscal."
                                    desc="Ideal para ME, EPP, empresas do Simples Nacional, Lucro Presumido, Lucro Real, prestadores de serviço, comércio, indústria e negócios em crescimento."
                                    items={CNPJ_ITEMS}
                                    cta="Quero análise para minha empresa"
                                    action={irParaSecao}
                                />
                            </Box>
                            <Box sx={{ flex: "1 1 300px", maxWidth: 600 }}>
                                <PublicCard
                                    type="CPF alta renda"
                                    color={C.cyan}
                                    title="Para profissionais e sócios que querem organizar a tributação da renda com inteligência."
                                    desc="Ideal para profissionais liberais, autônomos, consultores, creators, médicos, dentistas, advogados, investidores, executivos e sócios com renda relevante como pessoa física."
                                    items={CPF_ITEMS}
                                    cta="Quero análise para minha renda"
                                    action={irParaSecao}
                                />
                            </Box>
                        </Box>
                    </Container>
                </Box>

                <Box
                    component="section"
                    sx={{
                        py: { xs: 8, md: 12 },
                        background: `radial-gradient(circle at 10% 0%, rgba(255,215,0,0.11), transparent 28%), radial-gradient(circle at 90% 70%, rgba(144,16,176,0.18), transparent 35%), ${C.dark}`,
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: "center", maxWidth: 860, mx: "auto", mb: 6 }}>
                            <SectionLabel color={C.gold} center>Conquista</SectionLabel>
                            <SectionTitle center>
                                O objetivo é simples: fazer sobrar mais dinheiro no final do mês pra você,{" "}
                                <Box component="span" sx={{ color: C.gold }}>
                                    de forma legal e segura.
                                </Box>
                            </SectionTitle>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3 }}>
                            {BENEFITS.map((benefit) => (
                                <Box key={benefit.title}>
                                    <BenefitCard {...benefit} />
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                <Box component="section" id="autoridade" sx={{ py: { xs: 8, md: 12 }, backgroundColor: C.dark2, borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <Container maxWidth="lg">
                        <Box sx={{ maxWidth: 840, mb: 5.5 }}>
                            <SectionLabel color={C.cyan}>Comparativo técnico</SectionLabel>
                            <SectionTitle>
                                Planejamento tributário sério não é promessa de{" "}
                                <Box component="span" sx={{ color: C.cyan }}>
                                    pagar zero imposto.
                                </Box>
                            </SectionTitle>
                            <BodyText sx={{ mt: 2.2 }}>
                                Tem muita gente por aí prometendo economizar 30%, 40% do imposto sem nem ver os dados da empresa.
                                Isso não é consultoria, é chute. O Leão está de olho nesse tipo de planejamento. A nossa proposta é
                                diferente: diagnóstico real, recomendação com lastro, execução com segurança.
                            </BodyText>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3, mb: 5 }}>
                            <Box sx={{ flex: "1 1 300px", maxWidth: 600 }}>
                                <CheckListCard title="O que fazemos" items={FAZEMOS} color={C.green} />
                            </Box>
                            <Box sx={{ flex: "1 1 300px", maxWidth: 600 }}>
                                <CheckListCard title="O que não fazemos" items={NAO_FAZEMOS} color={C.magenta} negative />
                            </Box>
                        </Box>

                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 950, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: C.cyan, mb: 3 }}>
                            Perguntas que muita gente tem antes de começar
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2.4 }}>
                            {OBJECTIONS.map((item) => (
                                <Box sx={{ flex: "1 1 300px", maxWidth: 600 }} key={item.q}>
                                    <ObjectionCard {...item} />
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                <Box component="section" id="perguntas-frequentes" sx={{ py: { xs: 8, md: 12 }, backgroundColor: C.white, color: C.dark }}>
                    <Container maxWidth="md">
                        <Box sx={{ textAlign: "center", mb: 5 }}>
                            <SectionLabel color={C.dark} center>Perguntas frequentes</SectionLabel>
                            <SectionTitle dark center>Dúvidas frequentes sobre economia fiscal</SectionTitle>
                        </Box>

                        <Box sx={{ overflow: "hidden", borderRadius: 4, backgroundColor: C.white, border: "1px solid rgba(32,48,64,0.10)", boxShadow: "0 24px 70px rgba(32,48,64,0.10)" }}>
                            {FAQS.map((item, index) => (
                                <Accordion key={item.q} disableGutters elevation={0} sx={{ backgroundColor: C.white, borderBottom: index === FAQS.length - 1 ? "none" : "1px solid rgba(32,48,64,0.10)", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronDownIcon sx={{ color: C.green }} />} sx={{ px: { xs: 2.4, md: 3 }, py: 1.15, "& .MuiAccordionSummary-content": { my: 1.15 } }}>
                                        <Typography component="h3" sx={{ fontFamily: FONT_BODY, fontWeight: 950, color: C.dark, fontSize: 15, lineHeight: 1.35 }}>
                                            {item.q}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: { xs: 2.4, md: 3 }, pt: 0, pb: 2.6 }}>
                                        <Typography sx={{ fontFamily: FONT_BODY, color: "rgba(32,48,64,0.72)", fontSize: 14.2, lineHeight: 1.75 }}>
                                            {item.a}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </Container>
                </Box>

                <div ref={secaoRef}>
                    <FormularioNichos nicho="Economize" />
                </div>

                <Box
                    component="section"
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        py: { xs: 9, md: 13 },
                        background: `radial-gradient(circle at 12% 5%, rgba(0,230,118,0.18), transparent 32%), radial-gradient(circle at 85% 95%, rgba(224,0,144,0.20), transparent 35%), ${C.dark}`,
                    }}
                >
                    <FloatingGlow sx={{ left: -120, bottom: -110, backgroundColor: C.green }} />
                    <FloatingGlow sx={{ right: -140, bottom: -80, backgroundColor: C.magenta }} />

                    <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                        <Logo src={logoSrc} sx={{ width: 172, mx: "auto", mb: 3 }} />

                        <SectionLabel center>Dê o primeiro passo</SectionLabel>

                        <Typography component="h2" sx={{ fontFamily: FONT_TITLE, fontWeight: 950, fontSize: { xs: 37, md: 58 }, lineHeight: 1.02, letterSpacing: "-0.055em", color: C.white, mb: 2.4 }}>
                            Antes de aceitar que imposto alto é normal,{" "}
                            <Box component="span" sx={{ color: C.green }}>
                                descubra se existe um caminho melhor.
                            </Box>
                        </Typography>

                        <BodyText sx={{ maxWidth: 670, mx: "auto", mb: 3.7 }}>
                            O Leão não vai esperar você se organizar. Mas você pode chegar na frente, entender o que está saindo
                            do seu bolso a mais e montar um plano que funciona de verdade. Sem pressa, sem atalho, sem susto lá na frente.
                        </BodyText>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                            <MagentaButton action={irParaSecao}>Quero parar de pagar mais do que devo</MagentaButton>
                            <OutlineButton action={() => window.open(gerarLinkWhatsApp("Olá, quero pagar menos imposto."), "_blank")}>Falar com especialista agora</OutlineButton>
                        </Stack>

                        <Typography sx={{ mt: 2, fontFamily: FONT_BODY, color: "rgba(255,255,255,0.45)", fontSize: 12.5, lineHeight: 1.65 }}>
                            Sem promessa de número antes de ver os dados. Sem atalho que o Leão vai questionar depois.
                            Apenas estratégia fiscal de verdade.
                        </Typography>
                    </Container>
                </Box>
            </Box>

            <Footer />
        </Box>
    );
}
